<template>
  <div class="reset-password-container">
    <div class="reset-password-header">
      <el-dropdown @command="handleLocaleChange" popper-class="floating-dropdown-popper">
        <span class="locale-trigger">
          <el-icon><ChatRound /></el-icon>
          {{ currentLocaleName }}
        </span>
        <template #dropdown>
          <el-dropdown-menu>
            <el-dropdown-item command="en">English</el-dropdown-item>
            <el-dropdown-item command="zh">中文</el-dropdown-item>
          </el-dropdown-menu>
        </template>
      </el-dropdown>
      <el-button @click="appStore.toggleTheme" circle size="small">
        <el-icon><component :is="appStore.isDark ? Sunny : Moon" /></el-icon>
      </el-button>
    </div>
    <el-card class="reset-password-card">
      <template #header>
        <div class="card-header">
          <el-icon :size="32" color="var(--el-color-primary)"><Lock /></el-icon>
          <h2>{{ t('routes.reset-password.title') }}</h2>
        </div>
      </template>

      <el-steps :active="currentStep" finish-status="success" align-center class="steps-container">
        <el-step :title="t('routes.reset-password.step1-title')" />
        <el-step :title="t('routes.reset-password.step2-title')" />
        <el-step :title="t('routes.reset-password.step3-title')" />
        <el-step :title="t('routes.reset-password.step4-title')" />
      </el-steps>

      <!-- 步骤 1: 输入管理员用户名 + 图形验证码 -->
      <div v-if="currentStep === 0" class="step-content">
        <el-form :model="form" :rules="rules" ref="formRef" label-position="top">
          <el-form-item :label="t('routes.reset-password.username')" prop="username">
            <el-input
              v-model="form.username"
              :prefix-icon="User"
              :placeholder="t('routes.reset-password.username-placeholder')"
            />
          </el-form-item>

          <el-form-item :label="t('routes.reset-password.captcha')" prop="captcha">
            <div class="captcha-container">
              <el-input
                v-model="form.captcha"
                :placeholder="t('routes.reset-password.captcha-placeholder')"
                maxlength="4"
                style="flex: 1"
              />
              <div class="captcha-image-wrapper" @click="loadCaptcha">
                <el-icon v-if="captchaLoading" class="loading-icon is-loading"><Loading /></el-icon>
                <img v-else-if="captchaImage" :src="captchaImage" alt="captcha" class="captcha-image" />
                <span v-else class="captcha-placeholder">{{ t('common.loading') }}</span>
                <el-icon class="refresh-icon"><Refresh /></el-icon>
              </div>
            </div>
          </el-form-item>

          <el-form-item>
            <div class="button-group">
              <StandardButton action="backward" :custom-text="t('routes.reset-password.back-to-login')" @click="backToLogin" />
              <StandardButton
                action="submit"
                :custom-text="t('routes.reset-password.send-code')"
                @click="sendVerificationCode"
                :loading="loading"
              />
            </div>
          </el-form-item>
        </el-form>
      </div>

      <!-- 步骤 2: 输入验证码 -->
      <div v-if="currentStep === 1" class="step-content">
        <el-form :model="form" :rules="rules" ref="codeFormRef" label-position="top">
          <el-alert
            :title="codeSentMessage"
            type="success"
            :closable="false"
            show-icon
            class="alert-message"
          />

          <el-form-item :label="t('routes.reset-password.verification-code')" prop="code">
            <el-input
              v-model="form.code"
              :prefix-icon="Key"
              :placeholder="t('routes.reset-password.code-placeholder')"
              maxlength="6"
            />
          </el-form-item>

          <div class="resend-container">
            <span v-if="countdown > 0" class="countdown-text">
              {{ t('routes.reset-password.resend-countdown', { seconds: countdown }) }}
            </span>
            <StandardButton
              v-else
              action="retry"
              size="sm"
              display-mode="text-only"
              :custom-text="t('routes.reset-password.resend-code')"
              @click="resendCode"
            />
          </div>

          <el-form-item>
            <div class="button-group">
              <StandardButton action="prev" :custom-text="t('routes.reset-password.previous-step')" @click="previousStep" />
              <StandardButton
                action="confirm"
                :custom-text="t('routes.reset-password.verify')"
                @click="verifyCode"
                :loading="loading"
              />
            </div>
          </el-form-item>
        </el-form>
      </div>

      <!-- 步骤 3: 设置新密码 -->
      <div v-if="currentStep === 2" class="step-content">
        <el-form :model="form" :rules="rules" ref="passwordFormRef" label-position="top">
          <el-form-item :label="t('routes.reset-password.new-password')" prop="newPassword">
            <el-input
              v-model="form.newPassword"
              type="password"
              :prefix-icon="Lock"
              :placeholder="t('routes.reset-password.new-password-placeholder')"
              show-password
            />
          </el-form-item>

          <el-form-item :label="t('routes.reset-password.confirm-password')" prop="confirmPassword">
            <el-input
              v-model="form.confirmPassword"
              type="password"
              :prefix-icon="Lock"
              :placeholder="t('routes.reset-password.confirm-password-placeholder')"
              show-password
            />
          </el-form-item>

          <el-form-item>
            <div class="button-group">
              <StandardButton action="prev" :custom-text="t('routes.reset-password.previous-step')" @click="previousStep" />
              <StandardButton
                action="submit"
                :custom-text="t('routes.reset-password.submit')"
                @click="submitNewPassword"
                :loading="loading"
              />
            </div>
          </el-form-item>
        </el-form>
      </div>

      <!-- 步骤 4: 完成 -->
      <div v-if="currentStep === 3" class="step-content success-content">
        <el-result
          icon="success"
          :title="t('routes.reset-password.success-message')"
          :sub-title="t('routes.reset-password.success-description')"
        >
          <template #extra>
            <StandardButton action="confirm" :custom-text="t('routes.reset-password.back-to-login')" @click="backToLogin" />
          </template>
        </el-result>
      </div>
    </el-card>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { ElMessage } from 'element-plus'
import { useAppStore } from '../stores/app'
import { Lock, User, Key, ChatRound, Sunny, Moon, Refresh, Loading } from '@element-plus/icons-vue'
import type { FormInstance, FormRules } from 'element-plus'
import StandardButton from '@/components/StandardButton.vue'
import {
  getCaptcha as getResetCaptcha,
  sendVerificationCode as sendResetCodeApi,
  verifyCode as verifyResetCodeApi,
  resetPassword as resetPasswordApi
} from '@/api/auth'

const { t } = useI18n()
const router = useRouter()
const appStore = useAppStore()

const formRef = ref<FormInstance>()
const codeFormRef = ref<FormInstance>()
const passwordFormRef = ref<FormInstance>()
const loading = ref(false)
const currentStep = ref(0)
const countdown = ref(0)
let countdownTimer: number | null = null

const form = ref({
  username: '',
  code: '',
  newPassword: '',
  confirmPassword: '',
  captcha: '',
  captchaId: ''
})

/** 校验通过后的一次性重置令牌（API v5：verify-code → reset-password 两段式） */
const resetToken = ref('')
const captchaImage = ref('')
const captchaLoading = ref(false)

async function loadCaptcha() {
  captchaLoading.value = true
  try {
    const data = await getResetCaptcha()
    captchaImage.value = `data:image/svg+xml;base64,${data.captcha_image}`
    form.value.captchaId = data.captcha_id
    form.value.captcha = ''
  } catch (error) {
    console.error('[ResetPassword] Failed to load captcha:', error)
  } finally {
    captchaLoading.value = false
  }
}

const validateUsername = (__rule: any, value: any, callback: any) => {
  if (!value || !value.trim()) {
    callback(new Error(t('common.validation.required')))
  } else if (value.trim().length > 64) {
    callback(new Error(t('common.validation.maxLength').replace('{max}', '64')))
  } else {
    callback()
  }
}

const validateCode = (__rule: any, value: any, callback: any) => {
  if (!value) {
    callback(new Error(t('routes.reset-password.invalid-code')))
  } else if (!/^\d{6}$/.test(value)) {
    callback(new Error(t('routes.reset-password.invalid-code')))
  } else {
    callback()
  }
}

const validateConfirmPassword = (__rule: any, value: any, callback: any) => {
  if (!value) {
    callback(new Error(t('common.validation.required')))
  } else if (value !== form.value.newPassword) {
    callback(new Error(t('routes.reset-password.password-mismatch')))
  } else {
    callback()
  }
}

const validateCaptcha = (__rule: any, value: any, callback: any) => {
  if (!value || value.length !== 4) {
    callback(new Error(t('routes.reset-password.invalid-captcha')))
  } else {
    callback()
  }
}

const rules: FormRules = {
  username: [{ validator: validateUsername, trigger: 'blur' }],
  captcha: [{ validator: validateCaptcha, trigger: 'blur' }],
  code: [{ validator: validateCode, trigger: 'blur' }],
  newPassword: [
    { required: true, message: () => t('common.validation.required'), trigger: 'blur' },
    { min: 8, message: () => t('common.validation.minLength', { min: 8 }), trigger: 'blur' }
  ],
  confirmPassword: [{ validator: validateConfirmPassword, trigger: 'blur' }]
}

const currentLocaleName = computed(() => {
  return appStore.currentLocale === 'zh' ? '中文' : 'EN'
})

const codeSentMessage = computed(() => {
  return `${t('routes.reset-password.code-sent')}: ${form.value.username}`
})

function handleLocaleChange(lang: string) {
  appStore.setLocale(lang)
}

function backToLogin() {
  router.push('/login')
}

function startCountdown() {
  countdown.value = 60
  if (countdownTimer) {
    clearInterval(countdownTimer)
  }
  countdownTimer = window.setInterval(() => {
    countdown.value--
    if (countdown.value <= 0) {
      if (countdownTimer) {
        clearInterval(countdownTimer)
        countdownTimer = null
      }
    }
  }, 1000)
}

async function sendVerificationCode() {
  if (!formRef.value) return
  await formRef.value.validate(async (valid) => {
    if (valid) {
      loading.value = true
      try {
        await sendResetCodeApi({
          username: form.value.username.trim(),
          captcha: form.value.captcha,
          captcha_id: form.value.captchaId
        })
        ElMessage.success(t('routes.reset-password.code-sent'))
        currentStep.value = 1
        startCountdown()
      } catch (error: any) {
        const message = error?.message || t('common.messages.error')
        ElMessage.error(message)
        await loadCaptcha()
      } finally {
        loading.value = false
      }
    }
  })
}

async function resendCode() {
  await sendVerificationCodeWithoutValidation()
}

async function sendVerificationCodeWithoutValidation() {
  loading.value = true
  try {
    // 重发窗口（验证码 TTL 内）无需图形验证码，后端直接重发
    await sendResetCodeApi({
      username: form.value.username.trim(),
      captcha: '',
      captcha_id: ''
    })
    ElMessage.success(t('routes.reset-password.code-sent'))
    startCountdown()
  } catch (error: any) {
    const message = error?.message || t('common.messages.error')
    ElMessage.error(message)
  } finally {
    loading.value = false
  }
}

async function verifyCode() {
  if (!codeFormRef.value) return
  await codeFormRef.value.validate(async (valid) => {
    if (valid) {
      loading.value = true
      try {
        const res = await verifyResetCodeApi({
          username: form.value.username.trim(),
          code: form.value.code
        })
        resetToken.value = res.token  // 一次性重置令牌，下一步提交
        ElMessage.success(t('common.messages.success'))
        currentStep.value = 2
      } catch (error: any) {
        const message = error?.message || t('routes.reset-password.code-error')
        ElMessage.error(message)
      } finally {
        loading.value = false
      }
    }
  })
}

async function submitNewPassword() {
  if (!passwordFormRef.value) return
  await passwordFormRef.value.validate(async (valid) => {
    if (valid) {
      loading.value = true
      try {
        if (!resetToken.value) {
          throw new Error(t('routes.reset-password.code-error'))
        }
        await resetPasswordApi({
          token: resetToken.value,
          new_password: form.value.newPassword
        })
        currentStep.value = 3
      } catch (error: any) {
        const message = error?.message || t('common.messages.error')
        ElMessage.error(message)
      } finally {
        loading.value = false
      }
    }
  })
}

function previousStep() {
  if (currentStep.value > 0) {
    currentStep.value--
  }
}

onMounted(() => {
  loadCaptcha()
})

onUnmounted(() => {
  if (countdownTimer) {
    clearInterval(countdownTimer)
  }
})
</script>

<style scoped>
.reset-password-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: linear-gradient(135deg, var(--el-color-primary-light-9) 0%, var(--el-bg-color) 100%);
  padding: 20px;
}

.captcha-container {
  display: flex;
  gap: 12px;
  align-items: center;
}

.captcha-image-wrapper {
  width: 120px;
  height: 40px;
  border: 1px solid var(--el-border-color);
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  position: relative;
  transition: all 0.3s;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: var(--el-fill-color-light);
}

.captcha-image-wrapper:hover {
  border-color: var(--el-color-primary);
}

.captcha-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.refresh-icon {
  position: absolute;
  top: 50%;
  right: 6px;
  transform: translateY(-50%);
  color: var(--el-text-color-secondary);
  font-size: 14px;
  opacity: 0.7;
}

.captcha-placeholder {
  color: var(--el-text-color-secondary);
  font-size: 12px;
}

.loading-icon {
  font-size: 18px;
  color: var(--el-color-primary);
}

.reset-password-header {
  position: fixed;
  top: 20px;
  right: 20px;
  display: flex;
  gap: 12px;
  align-items: center;
}

.locale-trigger {
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  color: var(--el-text-color-regular);
  font-size: 14px;
}

.reset-password-card {
  width: 100%;
  max-width: 600px;
}

.card-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.card-header h2 {
  margin: 0;
  font-size: 20px;
  color: var(--el-text-color-primary);
}

.steps-container {
  margin-bottom: 32px;
}

.step-content {
  min-height: 200px;
}

.alert-message {
  margin-bottom: 20px;
}

.button-group {
  display: flex;
  width: 100%;
  gap: 12px;
  justify-content: space-between;
}

.button-group .el-button {
  flex: 1;
}

.resend-container {
  margin-top: -12px;
  margin-bottom: 20px;
  text-align: right;
}

.countdown-text {
  color: var(--el-text-color-secondary);
  font-size: 14px;
}

.success-content {
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
